import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Two-step "click to confirm" gate for a destructive action (inline delete).
 * The first `trigger()` arms the control — the caller reflects this via
 * `confirming` (e.g. swap the label to "Click to confirm"). A second
 * `trigger()` within `timeoutMs` runs `action`. If no confirm lands in time
 * the armed state auto-resets, so a stray first click never leaves a
 * hair-trigger delete sitting around.
 */
export function useConfirmClick(action: () => void | Promise<void>, timeoutMs = 3000) {
  const [confirming, setConfirming] = useState(false);
  const timer = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // Drop any pending timer if the host unmounts mid-confirm.
  useEffect(() => clear, [clear]);

  const reset = useCallback(() => {
    clear();
    setConfirming(false);
  }, [clear]);

  const trigger = useCallback(() => {
    if (!confirming) {
      setConfirming(true);
      clear();
      timer.current = window.setTimeout(() => {
        timer.current = null;
        setConfirming(false);
      }, timeoutMs);
      return;
    }
    clear();
    setConfirming(false);
    void action();
  }, [confirming, action, clear, timeoutMs]);

  return { confirming, trigger, reset };
}
